import { useState } from 'react';
import { cn } from '@rocco/utils/cn';
import { Input } from '@rocco/components/input';
import { Textarea } from '@rocco/components/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@rocco/components/select';
import { Checkbox } from '@rocco/components/checkbox';
import { OTHER_OPTION } from '~/modules/business-manager/forms/types';
import type { PublicEnquiryFormField } from '../types/public-enquiry-form';
import {
    isOtherAnswer,
    joinMultiValue,
    splitMultiValue,
} from '../util/custom-field-validation';
import { orderedCountries } from '../util/iso-countries';

interface CustomFormFieldsProps {
    fields: PublicEnquiryFormField[];
    answers: Record<string, string>;
    errors: Record<string, string>;
    disabled?: boolean;
    /** ISO alpha-2 code of where the visitor appears to be; listed first in Country fields. */
    visitorCountry?: string | null;
    onChange: (key: string, value: string) => void;
}

/**
 * The owner-defined questions on a Website form, rendered in the same
 * two-column grid as the fixed identity fields. Values live in the parent so
 * routing prediction and submit see them.
 */
export const CustomFormFields = ({
    fields,
    answers,
    errors,
    disabled,
    visitorCountry,
    onChange,
}: CustomFormFieldsProps) => (
    <>
        {fields.map((field) => (
            <CustomField
                key={field.key}
                field={field}
                value={answers[field.key] ?? ''}
                error={errors[field.key] ?? null}
                disabled={disabled}
                visitorCountry={visitorCountry}
                onChange={(value) => onChange(field.key, value)}
            />
        ))}
    </>
);

const CustomField = ({
    field,
    value,
    error,
    disabled,
    visitorCountry,
    onChange,
}: {
    field: PublicEnquiryFormField;
    value: string;
    error: string | null;
    disabled?: boolean;
    visitorCountry?: string | null;
    onChange: (value: string) => void;
}) => {
    const label = `${field.label}${field.required ? '*' : ''}`;
    const placeholder = field.placeholder.trim() || label;
    const full = field.width === 'Full' || field.type === 'Textarea';
    const inputId = `enquiry-field-${field.key}`;
    const errorId = `${inputId}-error`;
    const shared = {
        id: inputId,
        'aria-label': field.label,
        'aria-invalid': !!error,
        'aria-describedby': error ? errorId : undefined,
        disabled,
    };

    return (
        <div className={cn('space-y-2', full && 'md:col-span-2')}>
            {field.type === 'Select' || field.type === 'Country' ? (
                <SingleChoice
                    field={field}
                    value={value}
                    error={error}
                    disabled={disabled}
                    visitorCountry={visitorCountry}
                    placeholder={placeholder}
                    onChange={onChange}
                />
            ) : field.type === 'MultiSelect' ? (
                <MultiChoice
                    field={field}
                    value={value}
                    error={error}
                    disabled={disabled}
                    label={label}
                    inputId={inputId}
                    errorId={errorId}
                    onChange={onChange}
                />
            ) : field.type === 'Textarea' ? (
                <Textarea
                    {...shared}
                    className={cn(
                        'min-h-24 bg-[#fff] transition-colors duration-300 focus-visible:border-black',
                        error && 'border-red-600',
                    )}
                    placeholder={placeholder}
                    value={value}
                    maxLength={2000}
                    onChange={(event) => onChange(event.target.value)}
                />
            ) : (
                <Input
                    {...shared}
                    className={cn(
                        'bg-[#fff] transition-colors duration-300 focus-visible:border-black',
                        error && 'border-red-600',
                    )}
                    type={inputType(field.type)}
                    inputMode={inputMode(field.type)}
                    autoComplete={autoComplete(field.type)}
                    placeholder={placeholder}
                    value={value}
                    maxLength={255}
                    onChange={(event) => onChange(event.target.value)}
                />
            )}
            {error ? (
                <p id={errorId} className="text-sm text-red-600">
                    {error}
                </p>
            ) : null}
        </div>
    );
};

/**
 * A dropdown over the owner's options or the ISO country list. With "Other" allowed, an
 * extra choice reveals a text box and the typed text becomes the answer, so the server
 * stores what the visitor wrote rather than the word "Other".
 */
const SingleChoice = ({
    field,
    value,
    error,
    disabled,
    visitorCountry,
    placeholder,
    onChange,
}: {
    field: PublicEnquiryFormField;
    value: string;
    error: string | null;
    disabled?: boolean;
    visitorCountry?: string | null;
    placeholder: string;
    onChange: (value: string) => void;
}) => {
    const isCountry = field.type === 'Country';
    const allowOther = !isCountry && field.allowOther;
    const [otherMode, setOtherMode] = useState(() =>
        isOtherAnswer(field, value),
    );
    // Country answers are stored by name, so the option value is the name too.
    const options = isCountry
        ? orderedCountries(visitorCountry).map((country) => country.name)
        : field.options;
    const showOtherChoice = allowOther && !options.includes(OTHER_OPTION);
    const selectValue = otherMode ? OTHER_OPTION : value;

    return (
        <>
            <Select
                onValueChange={(next) => {
                    if (showOtherChoice && next === OTHER_OPTION) {
                        setOtherMode(true);
                        onChange('');
                        return;
                    }
                    setOtherMode(false);
                    onChange(next);
                }}
                value={selectValue}
                disabled={disabled}
            >
                <SelectTrigger
                    className={cn(
                        'w-full bg-[#fff]',
                        error && 'border-red-600',
                    )}
                    color="lightOutline"
                    aria-label={field.label}
                    aria-invalid={!!error}
                >
                    <SelectValue placeholder={placeholder} />
                </SelectTrigger>
                <SelectContent
                    className={cn(
                        'bg-transparent backdrop-blur-2xl',
                        isCountry && 'max-h-72',
                    )}
                    borderless
                >
                    {options.map((option) => (
                        <SelectItem
                            key={option}
                            className="py-4 pr-4 focus:bg-black/10"
                            value={option}
                        >
                            {option}
                        </SelectItem>
                    ))}
                    {showOtherChoice ? (
                        <SelectItem
                            key={OTHER_OPTION}
                            className="py-4 pr-4 focus:bg-black/10"
                            value={OTHER_OPTION}
                        >
                            {OTHER_OPTION}
                        </SelectItem>
                    ) : null}
                </SelectContent>
            </Select>
            {otherMode ? (
                <Input
                    aria-label={`${field.label}: your own answer`}
                    aria-invalid={!!error}
                    className={cn(
                        'bg-[#fff] transition-colors duration-300 focus-visible:border-black',
                        error && 'border-red-600',
                    )}
                    type="text"
                    placeholder="Please specify"
                    value={value}
                    maxLength={200}
                    disabled={disabled}
                    autoFocus
                    onChange={(event) => onChange(event.target.value)}
                />
            ) : null}
        </>
    );
};

/**
 * Tick-any-that-apply checkboxes. The answer is one choice per line; with "Other" allowed,
 * the last line may be the visitor's own text.
 */
const MultiChoice = ({
    field,
    value,
    error,
    disabled,
    label,
    inputId,
    errorId,
    onChange,
}: {
    field: PublicEnquiryFormField;
    value: string;
    error: string | null;
    disabled?: boolean;
    label: string;
    inputId: string;
    errorId: string;
    onChange: (value: string) => void;
}) => {
    const chosen = splitMultiValue(value);
    const otherText = chosen.find((c) => !field.options.includes(c)) ?? '';
    const [otherChecked, setOtherChecked] = useState(otherText !== '');
    const showOther = field.allowOther && !field.options.includes(OTHER_OPTION);

    const emit = (ticked: string[], other: string) =>
        onChange(
            joinMultiValue([
                ...field.options.filter((o) => ticked.includes(o)),
                ...(other.trim() !== '' ? [other.trim()] : []),
            ]),
        );

    return (
        <fieldset
            id={inputId}
            className="m-0 min-w-0 border-0 p-0"
            aria-invalid={!!error}
            aria-describedby={error ? errorId : undefined}
            disabled={disabled}
        >
            <legend className="mb-2 text-sm text-black/70">{label}</legend>
            <div className="flex flex-col gap-2">
                {field.options.map((option) => {
                    const id = `${inputId}-${slug(option)}`;
                    const checked = chosen.includes(option);
                    return (
                        <div key={option} className="flex items-center gap-2">
                            <Checkbox
                                id={id}
                                className={CHECKBOX_CLASS}
                                indicatorClassName={CHECKBOX_INDICATOR_CLASS}
                                checked={checked}
                                disabled={disabled}
                                onCheckedChange={(next) =>
                                    emit(
                                        next === true
                                            ? [...chosen, option]
                                            : chosen.filter(
                                                  (c) => c !== option,
                                              ),
                                        otherChecked ? otherText : '',
                                    )
                                }
                            />
                            <label
                                htmlFor={id}
                                className="cursor-pointer text-sm"
                            >
                                {option}
                            </label>
                        </div>
                    );
                })}
                {showOther ? (
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id={`${inputId}-other`}
                                className={CHECKBOX_CLASS}
                                indicatorClassName={CHECKBOX_INDICATOR_CLASS}
                                checked={otherChecked}
                                disabled={disabled}
                                onCheckedChange={(next) => {
                                    const on = next === true;
                                    setOtherChecked(on);
                                    if (!on) emit(chosen, '');
                                }}
                            />
                            <label
                                htmlFor={`${inputId}-other`}
                                className="cursor-pointer text-sm"
                            >
                                {OTHER_OPTION}
                            </label>
                        </div>
                        {otherChecked ? (
                            <Input
                                aria-label={`${field.label}: your own answer`}
                                className="bg-[#fff] transition-colors duration-300 focus-visible:border-black"
                                type="text"
                                placeholder="Please specify"
                                value={otherText}
                                maxLength={200}
                                disabled={disabled}
                                autoFocus
                                onChange={(event) =>
                                    emit(chosen, event.target.value)
                                }
                            />
                        ) : null}
                    </div>
                ) : null}
            </div>
        </fieldset>
    );
};

/** Pixel sizes: the embed page scales rem down, which would shrink a 1rem box to a dot. */
const CHECKBOX_CLASS = 'size-[16px] rounded-[3px] bg-[#fff]';
const CHECKBOX_INDICATOR_CLASS = 'size-[12px]';

const slug = (value: string) =>
    value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 40);

/** The native input type for a text-like custom field. */
export function inputType(type: PublicEnquiryFormField['type']): string {
    switch (type) {
        case 'Email':
            return 'email';
        case 'Phone':
            return 'tel';
        case 'Url':
            return 'url';
        case 'Text':
        case 'Select':
        case 'MultiSelect':
        case 'Country':
        case 'Textarea':
            return 'text';
        default: {
            const exhaustive: never = type;
            return exhaustive;
        }
    }
}

/** The virtual keyboard a text-like custom field asks for. */
export function inputMode(
    type: PublicEnquiryFormField['type'],
): 'email' | 'tel' | 'url' | 'text' {
    switch (type) {
        case 'Email':
            return 'email';
        case 'Phone':
            return 'tel';
        case 'Url':
            return 'url';
        case 'Text':
        case 'Select':
        case 'MultiSelect':
        case 'Country':
        case 'Textarea':
            return 'text';
        default: {
            const exhaustive: never = type;
            return exhaustive;
        }
    }
}

/** The browser autofill hint for a text-like custom field. */
export function autoComplete(type: PublicEnquiryFormField['type']): string {
    switch (type) {
        case 'Email':
            return 'email';
        case 'Phone':
            return 'tel';
        case 'Url':
            return 'url';
        case 'Text':
        case 'Select':
        case 'MultiSelect':
        case 'Country':
        case 'Textarea':
            return 'off';
        default: {
            const exhaustive: never = type;
            return exhaustive;
        }
    }
}
